import React from 'react';
import { daysOfWeek } from '~/constants/daysOfWeek';
import CircleChevronUpIcon from '../icons/CircleChevronUpIcon';
import CircleChevronDownIcon from '../icons/CircleChevronDownIcon';
import { ScheduleDay } from '~/interfaces/ScheduleDay';

interface Props {
  days: ScheduleDay[];
}

export default function SmallSchedule({ days }: Props) {
  const [expanded, setExpanded] = React.useState(false);
  const isThreeWeekSchedule = days.length === 21;
  const weekRowClasses = 'flex items-center justify-between pb-4';

  const renderDay = React.useCallback(
    (day: ScheduleDay) => {
      const baseClasses = 'w-[30px] flex justify-center items-center';
      const collapsedClasses = `h-[3px] rounded-lg transform-origin-center ${
        day.overnightGuardian ? 'bg-orange' : 'bg-blue'
      }`;
      const expandedClasses = `h-[30px] rounded-full text-[15px] font-medium text-green-primary-text ${
        day.overnightGuardian ? 'bg-orange-light-20' : 'bg-blue-light-20'
      }`;

      return (
        <span
          key={day.day}
          className={`${baseClasses} ${
            expanded ? expandedClasses : collapsedClasses
          } transform transition-all duration-300 ease-in-out`}
        >
          <span className={`transition-opacity duration-300 ${expanded ? 'opacity-100' : 'opacity-0'}`}>
            {day.dayNumber}
          </span>
        </span>
      );
    },
    [expanded]
  );

  const renderSchedule = React.useCallback(
    (startIndex: number, endIndex: number) => {
      const daysSection = days.slice(startIndex, endIndex);
      return daysSection.map((day: ScheduleDay) => renderDay(day));
    },
    [days, renderDay]
  );

  return (
    <div className="flex flex-col justify-between pt-4">
      <div className="flex items-center justify-between">
        {daysOfWeek.map((day, idx) => (
          <span
            key={`${day}-${idx}`}
            className="flex w-[30px] items-center justify-center pb-2 text-base font-bold text-brown"
          >
            {day}
          </span>
        ))}
      </div>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${expanded ? (isThreeWeekSchedule ? 'h-[138px]' : 'h-[184px]') : 'h-[38px]'}`}
      >
        <div>
          <div className={weekRowClasses}>{renderSchedule(0, 7)}</div>
          <div className={weekRowClasses}>{renderSchedule(7, 14)}</div>
          {expanded && (
            <>
              <div className={weekRowClasses}>{renderSchedule(14, 21)}</div>
              {!isThreeWeekSchedule && <div className={weekRowClasses}>{renderSchedule(21, 28)}</div>}
            </>
          )}
        </div>
      </div>
      <div className="pb-2 text-center">
        <button
          className="flex w-full cursor-pointer items-center justify-center border-none px-4 py-2 text-sm font-semibold text-brown"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          type="button"
        >
          {expanded ? 'Collapse' : 'Expand'} example{' '}
          <span className="inline-block pl-2">{expanded ? <CircleChevronUpIcon /> : <CircleChevronDownIcon />}</span>
        </button>
      </div>
    </div>
  );
}
